// client/src/components/ticketing/CheckinConsole.jsx
//
// Staff-facing check-in console: camera QR scan, comp walk-in registration,
// and a roster with manual override. Mobile-first — this is the screen
// staff use on a phone at the door. Camera scanning uses html5-qrcode's
// end-to-end Html5QrcodeScanner (handles camera permissions/UI itself).

import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const EMPTY_WALKIN = { name: '', email: '', phone: '', tier_id: '', num_tickets: 1 };

export default function CheckinConsole({ tenantId, eventId }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}`;

  const [tab, setTab] = useState('scan'); // scan | walkin | roster
  const [scanResult, setScanResult] = useState(null); // { ok, message, ticket }
  const scanBusyRef = useRef(false);

  const [walkinForm, setWalkinForm] = useState(EMPTY_WALKIN);
  const [walkinSaving, setWalkinSaving] = useState(false);
  const [walkinMessage, setWalkinMessage] = useState(null);

  const [roster, setRoster] = useState([]);
  const [rosterTotal, setRosterTotal] = useState(0);
  const [rosterStatus, setRosterStatus] = useState('');
  const [rosterPage, setRosterPage] = useState(1);

  const loadRoster = useCallback(async () => {
    try {
      const params = { page: rosterPage, limit: 50 };
      if (rosterStatus) params.status = rosterStatus;
      const { data } = await axios.get(`${base}/checkin/roster`, { params });
      if (data.success) {
        setRoster(data.tickets);
        setRosterTotal(data.total);
      }
    } catch (err) {
      console.error('Error loading roster:', err);
    }
  }, [base, rosterPage, rosterStatus]);

  useEffect(() => {
    if (tab === 'roster') loadRoster();
  }, [tab, loadRoster]);

  const handleScan = useCallback(
    async (qrToken) => {
      if (scanBusyRef.current) return;
      scanBusyRef.current = true;
      try {
        const { data } = await axios.post(`${base}/checkin/scan`, { qrToken });
        if (data.success) {
          setScanResult({ ok: true, message: `${data.ticket.attendee_name} — ${data.ticket.tier_name}`, ticket: data.ticket });
        } else {
          setScanResult({ ok: false, message: data.message });
        }
      } catch (err) {
        setScanResult({ ok: false, message: err?.response?.data?.message || 'Scan failed' });
      } finally {
        setTimeout(() => { scanBusyRef.current = false; }, 1500); // debounce re-scans of the same code
      }
    },
    [base]
  );

  useEffect(() => {
    if (tab !== 'scan') return undefined;
    let cancelled = false;
    let scanner = null;

    import('html5-qrcode').then(({ Html5QrcodeScanner }) => {
      if (cancelled) return;
      scanner = new Html5QrcodeScanner(
        'qr-reader',
        { fps: 10, qrbox: { width: 250, height: 250 } },
        false
      );
      scanner.render(
        (decodedText) => handleScan(decodedText),
        () => {} // ignore per-frame scan failures (no code in view)
      );
    });

    return () => {
      cancelled = true;
      if (scanner) scanner.clear().catch(() => {});
    };
  }, [tab, handleScan]);

  const submitWalkin = async (e) => {
    e.preventDefault();
    setWalkinSaving(true);
    setWalkinMessage(null);
    try {
      const { data } = await axios.post(`${base}/checkin/walkin`, {
        ...walkinForm,
        num_tickets: Number(walkinForm.num_tickets) || 1,
      });
      if (data.success) {
        setWalkinMessage({ ok: true, text: `Checked in — ${data.tickets.length} ticket${data.tickets.length === 1 ? '' : 's'}` });
        setWalkinForm(EMPTY_WALKIN);
      } else {
        setWalkinMessage({ ok: false, text: data.message });
      }
    } catch (err) {
      setWalkinMessage({ ok: false, text: err?.response?.data?.message || 'Walk-in registration failed' });
    } finally {
      setWalkinSaving(false);
    }
  };

  const overrideTicket = async (ticketId, status) => {
    try {
      await axios.post(`${base}/checkin/${ticketId}/override`, { status });
      loadRoster();
    } catch (err) {
      console.error('Override failed:', err);
    }
  };

  return (
    <div className="max-w-lg mx-auto p-3">
      <h1 className="text-lg font-semibold mb-3">Check-in</h1>

      <div className="flex gap-2 mb-4 border-b border-white/10">
        {[
          { key: 'scan', label: 'Scan' },
          { key: 'walkin', label: 'Walk-in' },
          { key: 'roster', label: 'Roster' },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.key ? 'border-brand-orange text-brand-orange' : 'border-transparent opacity-60'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'scan' && (
        <div>
          <div id="qr-reader" className="mb-3" />
          {scanResult && (
            <div
              className={`px-3 py-2 rounded text-sm font-medium ${
                scanResult.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {scanResult.ok ? '✓ ' : '✗ '}{scanResult.message}
            </div>
          )}
        </div>
      )}

      {tab === 'walkin' && (
        <form onSubmit={submitWalkin} className="space-y-3">
          {walkinMessage && (
            <div className={`px-3 py-2 rounded text-sm ${walkinMessage.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {walkinMessage.text}
            </div>
          )}
          <input
            className="w-full px-3 py-2 rounded border bg-transparent"
            placeholder="Full name"
            value={walkinForm.name}
            onChange={(e) => setWalkinForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <input
            className="w-full px-3 py-2 rounded border bg-transparent"
            type="email"
            placeholder="Email"
            value={walkinForm.email}
            onChange={(e) => setWalkinForm((f) => ({ ...f, email: e.target.value }))}
            required
          />
          <input
            className="w-full px-3 py-2 rounded border bg-transparent"
            placeholder="Phone (optional)"
            value={walkinForm.phone}
            onChange={(e) => setWalkinForm((f) => ({ ...f, phone: e.target.value }))}
          />
          <input
            className="w-full px-3 py-2 rounded border bg-transparent"
            placeholder="Tier ID"
            value={walkinForm.tier_id}
            onChange={(e) => setWalkinForm((f) => ({ ...f, tier_id: e.target.value }))}
            required
          />
          <input
            className="w-full px-3 py-2 rounded border bg-transparent"
            type="number"
            min="1"
            placeholder="Number of tickets"
            value={walkinForm.num_tickets}
            onChange={(e) => setWalkinForm((f) => ({ ...f, num_tickets: e.target.value }))}
          />
          <p className="text-xs opacity-60">
            Walk-in registration is comp/free. For a paid walk-in, use the event's public registration link on this
            device, then scan the resulting ticket.
          </p>
          <button
            type="submit"
            disabled={walkinSaving}
            className="w-full px-4 py-2 rounded bg-cobalt text-white font-semibold disabled:opacity-50"
          >
            {walkinSaving ? 'Checking in…' : 'Check in walk-in'}
          </button>
        </form>
      )}

      {tab === 'roster' && (
        <div>
          <select
            className="w-full px-3 py-2 rounded border bg-transparent mb-3"
            value={rosterStatus}
            onChange={(e) => { setRosterStatus(e.target.value); setRosterPage(1); }}
          >
            <option value="">All statuses</option>
            <option value="active">Not checked in</option>
            <option value="checked_in">Checked in</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <div className="text-xs opacity-60 mb-2">{rosterTotal} total</div>
          <ul className="space-y-2">
            {roster.map((ticket) => (
              <li key={ticket.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 p-3 text-sm">
                <div>
                  <div className="font-semibold">{ticket.attendee_name}</div>
                  <div className="opacity-70 text-xs">{ticket.tier_name} · {ticket.status}</div>
                </div>
                {ticket.status === 'checked_in' ? (
                  <button type="button" onClick={() => overrideTicket(ticket.id, 'active')} className="px-2 py-1 rounded border text-xs">
                    Undo
                  </button>
                ) : (
                  <button type="button" onClick={() => overrideTicket(ticket.id, 'checked_in')} className="px-2 py-1 rounded bg-cobalt text-white text-xs">
                    Check in
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
